<template>
  <div class="md-root" :class="{ 'light-mode': !isDarkMode }">
    <!-- Sleek Header -->
    <div class="md-header">
      <div class="d-flex align-center" style="gap: 12px">
        <v-btn icon :dark="isDarkMode" :light="!isDarkMode" small @click="$emit('back')" class="md-back-btn">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <div class="d-flex align-center">
            <div class="text-subtitle-1 font-weight-bold white--text mr-2">{{ monthName }}</div>
            <v-chip x-small label :color="isPlanPublished ? 'green accent-3' : 'amber darken-1'" class="black--text font-weight-bold mr-2">
              <v-icon left x-small>{{ isPlanPublished ? "mdi-eye-check-outline" : "mdi-eye-off-outline" }}</v-icon>
              {{ isPlanPublished ? "Freigegeben" : "Entwurf · fürs Team unsichtbar" }}
            </v-chip>
            <!-- Studios dropdown -->
            <v-menu offset-y :close-on-content-click="false" :dark="isDarkMode" :light="!isDarkMode" v-if="availableStudios.length > 1">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  x-small
                  outlined
                  class="md-action-btn"
                  v-bind="attrs"
                  v-on="on"
                  :dark="isDarkMode"
                  :light="!isDarkMode"
                  style="height: 20px !important">
                  <v-icon left x-small>mdi-domain</v-icon>
                  <span>Studios einblenden</span>
                  <v-icon right x-small class="ml-1">mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list dense :class="isDarkMode ? 'grey darken-3' : 'white'">
                <v-list-item
                  v-for="studio in availableStudios"
                  :key="studio._id"
                  v-if="studio._id !== studioId"
                  @click="toggleAdditionalStudio(studio._id)">
                  <v-list-item-action class="mr-2 my-1">
                    <v-checkbox
                      :input-value="activeAdditionalStudioIds.includes(studio._id)"
                      dense
                      hide-details
                      readonly
                      color="green accent-3"></v-checkbox>
                  </v-list-item-action>
                  <v-list-item-content class="py-1">
                    <v-list-item-title :class="isDarkMode ? 'white--text' : 'black--text'">{{ studio.name }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
          <div class="text-caption" style="color: rgba(255, 255, 255, 0.35)">Einsatzplan bearbeiten</div>
        </div>
      </div>
      <div class="d-flex align-center" style="gap: 8px">
        <v-btn
          v-if="canPublishPlan && !isPlanPublished"
          small
          depressed
          color="green accent-3"
          class="md-action-btn md-publish-btn black--text font-weight-bold"
          :disabled="loading || primaryAssignedShiftCount === 0"
          @click="publishPlanDialog = true">
          <v-icon left x-small>mdi-publish</v-icon>
          Plan freigeben
        </v-btn>
        <v-btn
          v-if="canPublishPlan && isPlanPublished"
          small
          outlined
          color="warning"
          class="md-action-btn font-weight-bold"
          :disabled="loading || unpublishingPlan"
          @click="unpublishPlanDialog = true">
          <v-icon left x-small>mdi-eye-off-outline</v-icon>
          Freigabe zurückziehen
        </v-btn>
        <v-btn small depressed class="md-action-btn" @click="openEmployeeSettings()" :disabled="loading">
          <v-icon left x-small>mdi-account-cog</v-icon>
          Mitarbeiter-Einstellungen
        </v-btn>
        <v-btn
          small
          depressed
          color="indigo"
          class="md-action-btn font-weight-bold"
          @click="toggleAssistantView"
          :disabled="!editMode && !canUseAssistant"
          :title="!editMode && !canUseAssistant ? 'Assistent wartet auf vollständige Sollstunden' : ''">
          <v-icon left x-small>{{ editMode ? "mdi-calendar-month" : "mdi-auto-fix" }}</v-icon>
          {{ editMode ? "Einsatzplan anzeigen" : "Assistent öffnen" }}
        </v-btn>
        <v-tooltip v-if="!editMode" bottom :disabled="canClearEntirePlan">
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-btn
                small
                depressed
                color="error"
                class="md-action-btn"
                @click="openDeleteDialog"
                :loading="deleting"
                :disabled="!canClearEntirePlan">
                <v-icon left x-small>mdi-delete-sweep</v-icon> Plan leeren
              </v-btn>
            </span>
          </template>
          <span>Zusätzliche Studios zuerst ausblenden, damit keine fremden Studio-Schichten gelöscht werden.</span>
        </v-tooltip>
        <v-btn
          small
          depressed
          color="warning"
          class="md-action-btn black--text font-weight-bold"
          @click="confirmDeleteUnassignedDialog = true"
          :loading="deletingUnassigned"
          v-if="!editMode && unassignedShifts.length > 0">
          <v-icon left x-small>mdi-delete-sweep</v-icon> Offene löschen
        </v-btn>
        <v-btn small depressed class="md-refresh-btn" @click="refreshData" :loading="loading || deleting || deletingUnassigned">
          <v-icon left x-small>mdi-refresh</v-icon> Aktualisieren
        </v-btn>
        <!-- Sun/Moon Toggle Button -->
        <v-btn icon @click="toggleTheme" class="ml-2 theme-toggle-btn" :title="isDarkMode ? 'Hellmodus aktivieren' : 'Dunkelmodus aktivieren'">
          <v-icon :color="isDarkMode ? 'amber' : 'blue-grey'">
            {{ isDarkMode ? "mdi-weather-sunny" : "mdi-weather-night" }}
          </v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Content -->
    <div class="md-content">
      <v-alert
        v-if="workHoursSummaryLoading || workHoursSummaryWarning"
        dense
        text
        :type="workHoursSummaryWarning ? 'warning' : 'info'"
        class="md-summary-warning mb-0">
        <div class="d-flex align-center flex-wrap" style="gap: 10px">
          <v-progress-circular v-if="workHoursSummaryLoading" indeterminate size="18" width="2"></v-progress-circular>
          <span class="flex-grow-1">
            {{ workHoursSummaryWarning || "Sollstunden werden zentral geprüft. Der Entwurf bleibt bis zur manuellen Freigabe fürs Team unsichtbar." }}
          </span>
          <v-btn
            v-if="workHoursSummaryWarning && !workHoursSummaryLoading"
            x-small
            outlined
            color="warning"
            class="text-none"
            @click="retryWorkHoursSummaries">
            <v-icon left x-small>mdi-refresh</v-icon>Erneut berechnen
          </v-btn>
        </div>
      </v-alert>
      <shift-management-assistant
        v-if="!loading && editMode"
        :studio-id="studioId"
        :start-date="startDate"
        :end-date="endDate"
        :available-shifts="settings.shifts"
        :school-holidays="settings.schoolHolidays || []"
        :public-holidays="publicHolidays"
        :employees="assignedEmployees"
        :previous-context="previousContext"
        :is-dark-mode="isDarkMode"
        :initial-shift-id="assistantFocusShiftId"
        @back="closeAssistant"
        @plan-draft="planPublicationStatus = 'draft'"
        @view-plan="closeAssistant" />
      <shift-management-roster-view
        v-else-if="!loading && !editMode && (assignedPlanData.length > 0 || unassignedShifts.length > 0)"
        :plan-data="assignedPlanData"
        :external-shifts="externalShifts"
        :studioId="studioId"
        :availableShifts="settings.shifts || []"
        :unassignedShifts="unassignedShifts"
        :employees="assignedEmployees"
        :available-employees="availableEmployeesToAdd"
        :available-studios="availableStudios"
        :start-date="startDate"
        :school-holidays="settings.schoolHolidays || []"
        :public-holidays="publicHolidays"
        :previous-context="previousContext"
        :loading="loading"
        :is-dark-mode="isDarkMode"
        @add-employee="onAddEmployee"
        @refresh="refreshData"
        @open-assistant="openAssistantForShift"
        @open-employee-settings="openEmployeeSettings" />
      <div v-else-if="loading" class="md-loading">
        <v-progress-circular indeterminate color="green accent-3" size="56" width="3"></v-progress-circular>
        <p class="mt-4" style="color: rgba(255, 255, 255, 0.4)">Lade Planungsdaten…</p>
      </div>
      <div v-else class="md-loading">
        <v-icon size="48" color="rgba(255,255,255,0.15)">mdi-calendar-blank-outline</v-icon>
        <p class="mt-3" style="color: rgba(255, 255, 255, 0.4)">Keine Zuweisungen gefunden.</p>
        <v-btn small depressed color="green accent-3" class="mt-3 black--text" @click="$emit('back')">Zurück</v-btn>
      </div>
    </div>

    <!-- Delete Dialog -->
    <v-dialog v-model="confirmDeleteDialog" max-width="480px" :dark="isDarkMode" :light="!isDarkMode">
      <v-card :dark="isDarkMode" :light="!isDarkMode" class="rounded-xl" :class="isDarkMode ? '' : 'white'">
        <v-card-title class="d-flex align-center" style="gap: 8px">
          <v-icon color="red">mdi-delete-sweep</v-icon>
          <span style="font-size: 1rem">Plan komplett leeren?</span>
        </v-card-title>
        <v-card-text class="text-body-2">
          Möchtest du wirklich alle Schichten in diesem Monat löschen? Das kann nicht rückgängig gemacht werden.
          <v-alert v-if="!canClearEntirePlan" dense text type="warning" class="mt-3 mb-0">
            Der Plan kann nur geleert werden, wenn keine zusätzlichen Studios eingeblendet sind.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDeleteDialog = false" :disabled="deleting">Abbrechen</v-btn>
          <v-btn color="red" text @click="executeDelete" :loading="deleting" :disabled="!canClearEntirePlan" class="font-weight-bold">
            Ja, leeren
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Delete Unassigned Dialog -->
    <v-dialog v-model="confirmDeleteUnassignedDialog" max-width="480px" :dark="isDarkMode" :light="!isDarkMode">
      <v-card :dark="isDarkMode" :light="!isDarkMode" class="rounded-xl" :class="isDarkMode ? '' : 'white'">
        <v-card-title class="d-flex align-center" style="gap: 8px">
          <v-icon color="warning">mdi-delete-sweep</v-icon>
          <span style="font-size: 1rem" :class="isDarkMode ? 'white--text' : 'black--text'">Offene Schichten löschen?</span>
        </v-card-title>
        <v-card-text class="text-body-2" :class="isDarkMode ? 'white--text' : 'black--text'">
          Möchtest du wirklich alle noch offenen/unbesetzten Schichten in diesem Monat unwiderruflich löschen?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDeleteUnassignedDialog = false" :disabled="deletingUnassigned">Abbrechen</v-btn>
          <v-btn color="warning" text @click="executeDeleteUnassigned" :loading="deletingUnassigned" class="font-weight-bold">Ja, löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="publishPlanDialog" max-width="520px" :dark="isDarkMode" :light="!isDarkMode" persistent>
      <v-card :dark="isDarkMode" :light="!isDarkMode" class="rounded-xl">
        <v-card-title class="d-flex align-center" style="gap: 10px">
          <v-icon color="green accent-3">mdi-publish</v-icon>
          <span class="text-subtitle-1 font-weight-bold">Einsatzplan fürs Team freigeben?</span>
        </v-card-title>
        <v-card-text>
          Nach der Freigabe sind die eingeteilten Schichten für {{ monthName }} im Arbeitskalender und in den persönlichen Monatsplänen sichtbar.
          <v-alert v-if="primaryUnassignedShiftCount > 0" dense text type="warning" class="mt-4 mb-0">
            {{ primaryUnassignedShiftCount }} offene {{ primaryUnassignedShiftCount === 1 ? "Schicht ist" : "Schichten sind" }} noch nicht zugewiesen
            und werden dem Team nicht angezeigt.
          </v-alert>
        </v-card-text>
        <v-card-actions class="px-5 pb-4">
          <v-spacer></v-spacer>
          <v-btn text :disabled="publishingPlan" @click="publishPlanDialog = false">Noch nicht</v-btn>
          <v-btn color="green accent-3" class="black--text font-weight-bold" depressed :loading="publishingPlan" @click="publishPlan">
            Jetzt aufschalten
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="unpublishPlanDialog" max-width="520px" :dark="isDarkMode" :light="!isDarkMode" persistent>
      <v-card :dark="isDarkMode" :light="!isDarkMode" class="rounded-xl">
        <v-card-title class="d-flex align-center" style="gap: 10px">
          <v-icon color="warning">mdi-eye-off-outline</v-icon>
          <span class="text-subtitle-1 font-weight-bold">Freigabe zurückziehen?</span>
        </v-card-title>
        <v-card-text>
          Der Einsatzplan für {{ monthName }} wird wieder zum Entwurf. Die eingeteilten Schichten verschwinden dadurch sofort aus dem Arbeitskalender
          und den persönlichen Monatsplänen des Teams.
          <v-alert dense text type="info" class="mt-4 mb-0">Du kannst den Plan nach weiteren Anpassungen jederzeit erneut freigeben.</v-alert>
        </v-card-text>
        <v-card-actions class="px-5 pb-4">
          <v-spacer></v-spacer>
          <v-btn text :disabled="unpublishingPlan" @click="unpublishPlanDialog = false">Freigegeben lassen</v-btn>
          <v-btn color="warning" class="black--text font-weight-bold" depressed :loading="unpublishingPlan" @click="unpublishPlan">
            Freigabe zurückziehen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <shift-management-month-employee-settings-dialog
      v-model="employeeSettingsDialog"
      :studio-id="studioId"
      :month-id="monthId"
      :start-date="startDate"
      :end-date="endDate"
      :employees="allEmployees"
      :summaries="workHoursSummaries"
      :available-shifts="settings.shifts || []"
      :plan-shifts="assignedPlanData"
      :school-holidays="settings.schoolHolidays || []"
      :public-holidays="publicHolidays"
      :initial-employee-id="employeeSettingsInitialId"
      :is-dark-mode="isDarkMode"
      @saved="onMonthlySettingsSaved" />

    <v-dialog v-model="settingsSavedDialog" max-width="520px" :dark="isDarkMode" :light="!isDarkMode" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="d-flex align-center">
          <v-icon color="green accent-3" class="mr-3">mdi-check-circle</v-icon>
          <span class="text-subtitle-1 font-weight-bold">Monatseinstellungen gespeichert</span>
        </v-card-title>
        <v-card-text>
          Die Einstellungen für <strong>{{ settingsSavedEmployeeName }}</strong> gelten jetzt für {{ monthName }}. Alle bestehenden Schichtzuweisungen
          sind unverändert geblieben.
          <v-alert dense text type="info" class="mt-4 mb-0">
            Es wird nichts automatisch neu zugewiesen. Anpassungen am Einsatzplan erfolgen kontrolliert im Assistenten.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="settingsSavedDialog = false">Fertig</v-btn>
          <v-btn color="indigo" text class="font-weight-bold" @click="openAssistantAfterSettings" :disabled="!canUseAssistant">
            <v-icon left small>mdi-auto-fix</v-icon>
            Im Assistenten prüfen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import ShiftManagementRosterView from "./shiftManagementRosterView.vue"
import ShiftManagementAssistant from "./shiftManagementAssistant.vue"
import ShiftManagementMonthEmployeeSettingsDialog from "./shiftManagementMonthEmployeeSettingsDialog.vue"

export default {
  name: "ShiftManagementMonthDetail",
  components: {
    ShiftManagementRosterView,
    ShiftManagementAssistant,
    ShiftManagementMonthEmployeeSettingsDialog,
  },
  props: {
    studioId: {
      type: String,
      required: true,
    },
    monthId: {
      type: String, // Format: "YYYY-MM"
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      settings: {},
      allEmployees: [],
      assignedPlanData: [],
      unassignedShifts: [],
      externalShifts: [],
      assignedEmployees: [],
      workHoursSummaries: {},
      workHoursSummaryWarning: "",
      workHoursSummaryLoading: false,
      workHoursSummaryTimeoutMs: 12000,
      refreshRequestId: 0,
      shiftSourceStudioIds: {},
      publicHolidays: [],
      previousContext: null,
      confirmDeleteDialog: false,
      deleting: false,
      confirmDeleteUnassignedDialog: false,
      deletingUnassigned: false,
      manuallyAddedEmployeeIds: [],
      availableEmployeesToAdd: [],
      editMode: false,
      isDarkMode: localStorage.getItem("shiftManagementDarkMode") !== "false",
      activeAdditionalStudioIds: [],
      availableStudios: [],
      employeeSettingsDialog: false,
      employeeSettingsInitialId: null,
      assistantFocusShiftId: null,
      settingsSavedDialog: false,
      settingsSavedEmployeeName: "",
      planPublicationStatus: "published",
      publishPlanDialog: false,
      publishingPlan: false,
      unpublishPlanDialog: false,
      unpublishingPlan: false,
    }
  },
  computed: {
    monthName() {
      return this.$moment(this.monthId, "YYYY-MM").format("MMMM YYYY")
    },
    startDate() {
      return this.$moment(this.monthId, "YYYY-MM").startOf("month").format("YYYY-MM-DD")
    },
    endDate() {
      return this.$moment(this.monthId, "YYYY-MM").endOf("month").format("YYYY-MM-DD")
    },
    canClearEntirePlan() {
      return this.activeAdditionalStudioIds.length === 0
    },
    canUseAssistant() {
      return (
        !this.loading &&
        !this.workHoursSummaryLoading &&
        this.assignedEmployees.length > 0 &&
        this.assignedEmployees.every(
          (employee) => employee.workHoursSummaryUnavailable !== true && this.isValidWorkHoursSummary(employee.hoursSummary),
        )
      )
    },
    isPlanPublished() {
      return this.planPublicationStatus === "published"
    },
    canPublishPlan() {
      return userService.hasRight("g_employeemanager")
    },
    primaryAssignedShiftCount() {
      return this.assignedPlanData.filter((shift) => {
        const shiftId = String(shift?._id || "")
        const sourceStudioId = String(this.shiftSourceStudioIds[shiftId] || shift?.studio?._id || shift?.studio || this.studioId)
        return sourceStudioId === String(this.studioId)
      }).length
    },
    primaryUnassignedShiftCount() {
      return this.unassignedShifts.filter((shift) => {
        const shiftId = String(shift?._id || "")
        const sourceStudioId = String(this.shiftSourceStudioIds[shiftId] || shift?.studio?._id || shift?.studio || this.studioId)
        return sourceStudioId === String(this.studioId)
      }).length
    },
  },
  async created() {
    await this.initData()
  },
  watch: {
    monthId() {
      this.editMode = false
      this.planPublicationStatus = "published"
      this.initData()
    },
  },
  methods: {
    async publishPlan() {
      if (!this.canPublishPlan || this.publishingPlan) return
      if (this.primaryAssignedShiftCount === 0) {
        this.$toast.info("Es muss mindestens eine Schicht zugewiesen sein, bevor der Plan freigegeben wird.")
        return
      }

      this.publishingPlan = true
      try {
        const result = await api.shiftManagement.publishPlan(this.studioId, this.monthId)
        this.planPublicationStatus = "published"
        this.publishPlanDialog = false
        const assignedCount = Number(result?.assignedShifts) || this.primaryAssignedShiftCount
        this.$toast.success(`Einsatzplan freigegeben: ${assignedCount} eingeteilte Schichten sind jetzt fürs Team sichtbar.`)
      } catch (error) {
        this.$toast.error(error?.response?.data?.message || "Der Einsatzplan konnte nicht freigegeben werden.")
      } finally {
        this.publishingPlan = false
      }
    },
    async unpublishPlan() {
      if (!this.canPublishPlan || this.unpublishingPlan) return

      this.unpublishingPlan = true
      try {
        await api.shiftManagement.unpublishPlan(this.studioId, this.monthId)
        this.planPublicationStatus = "draft"
        this.unpublishPlanDialog = false
        this.$toast.success("Freigabe zurückgezogen: Der Einsatzplan ist für das Team wieder unsichtbar.")
      } catch (error) {
        this.$toast.error(error?.response?.data?.message || "Die Freigabe konnte nicht zurückgezogen werden.")
      } finally {
        this.unpublishingPlan = false
      }
    },
    withTimeout(promise, timeoutMs, message) {
      let timeoutId
      const timeout = new Promise((resolve, reject) => {
        timeoutId = setTimeout(() => reject(new Error(message || "Zeitüberschreitung")), timeoutMs)
      })
      return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId))
    },
    isValidWorkHoursSummary(summary) {
      const total = summary && summary.total
      if (!total) return false
      const hasNumber = (value) => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value))
      return hasNumber(total.netTargetHours) && hasNumber(total.plannedHours)
    },
    toggleAssistantView() {
      if (this.editMode) {
        this.closeAssistant()
        return
      }
      if (!this.canUseAssistant) {
        this.$toast.info("Der Assistent wird freigegeben, sobald alle Sollstunden zuverlässig berechnet sind.")
        return
      }
      this.assistantFocusShiftId = null
      this.editMode = true
    },
    async closeAssistant() {
      this.editMode = false
      this.assistantFocusShiftId = null
      await this.refreshData()
    },
    openAssistantForShift(payload = {}) {
      if (!this.canUseAssistant) {
        this.$toast.info("Der Assistent wird freigegeben, sobald alle Sollstunden zuverlässig berechnet sind.")
        return
      }
      this.assistantFocusShiftId = payload.shiftId || null
      this.editMode = true
    },
    openEmployeeSettings(employee = null) {
      this.employeeSettingsInitialId = employee && typeof employee === "object" ? employee.employeeWebsiteId || employee._id : employee || null
      this.employeeSettingsDialog = true
    },
    openDeleteDialog() {
      if (!this.canClearEntirePlan) {
        this.$toast.info("Blende zusätzliche Studios aus, bevor du den Plan leerst.")
        return
      }
      this.confirmDeleteDialog = true
    },
    toggleTheme() {
      this.isDarkMode = !this.isDarkMode
      localStorage.setItem("shiftManagementDarkMode", String(this.isDarkMode))
    },
    toggleAdditionalStudio(studioId) {
      const idx = this.activeAdditionalStudioIds.indexOf(studioId)
      if (idx > -1) {
        this.activeAdditionalStudioIds.splice(idx, 1)
      } else {
        this.activeAdditionalStudioIds.push(studioId)
      }
      this.refreshData()
    },
    async initData() {
      this.loading = true
      try {
        const [settingsRes, publicHolidaysRes, availableStudiosRes] = await Promise.all([
          api.shiftManagement.fetchSettings(this.studioId),
          api.shiftManagement.fetchPublicHolidays(this.studioId, this.startDate, this.endDate),
          api.fetchAllowedAdminStudios(),
        ])
        this.settings = settingsRes
        this.publicHolidays = publicHolidaysRes || []
        this.availableStudios = availableStudiosRes || []
        await this.refreshData()
      } catch (err) {
        console.error("Fehler beim Initialisieren des Monats", err)
        this.$toast.error("Konnte Basisdaten nicht laden.")
      } finally {
        this.loading = false
      }
    },
    async refreshData() {
      const requestId = ++this.refreshRequestId
      this.loading = true
      this.workHoursSummaryWarning = ""
      this.workHoursSummaryLoading = true
      this.workHoursSummaries = {}
      try {
        const studiosToFetch = [this.studioId, ...this.activeAdditionalStudioIds]

        const promises = studiosToFetch.map(async (studioId) => {
          const [employees, plan, prevContext] = await Promise.all([
            api.shiftManagement.fetchShiftManagementEmployee(studioId),
            api.shiftManagement.fetchPlan(studioId, this.startDate, this.endDate, true),
            api.shiftManagement.fetchPreviousMonthContext(studioId, this.startDate).catch(() => null),
          ])

          const taggedEmployees = (employees || []).map((emp) => ({
            ...emp,
            loadedFromStudioId: studioId,
          }))

          const responseData = plan && plan.data ? plan.data : plan
          const shifts = responseData && responseData.shifts ? responseData.shifts : Array.isArray(responseData) ? responseData : []
          const externalShifts = responseData && responseData.externalShifts ? responseData.externalShifts : []

          return {
            studioId,
            employees: taggedEmployees,
            shifts,
            externalShifts,
            planPublications: responseData && Array.isArray(responseData.planPublications) ? responseData.planPublications : [],
            prevContext,
          }
        })

        const results = await Promise.all(promises)
        if (requestId !== this.refreshRequestId) return

        const primaryPlan = results.find((result) => String(result.studioId) === String(this.studioId))
        const primaryPublication = primaryPlan?.planPublications?.find((publication) => publication.month === this.monthId)
        this.planPublicationStatus = primaryPublication?.status || "published"

        const allEmpsMap = new Map()
        results.forEach((res) => {
          res.employees.forEach((emp) => {
            const employeeKey = String(emp._id)
            const existing = allEmpsMap.get(employeeKey)
            if (!existing) {
              allEmpsMap.set(employeeKey, emp)
              return
            }

            const studiosById = new Map([...(existing.studio || []), ...(emp.studio || [])].map((studio) => [String(studio._id), studio]))
            allEmpsMap.set(employeeKey, {
              ...emp,
              ...existing,
              studio: Array.from(studiosById.values()),
              shiftPlanningSettings: {
                ...(emp.shiftPlanningSettings || {}),
                ...(existing.shiftPlanningSettings || {}),
              },
              // `studiosToFetch` beginnt mit dem Hauptstudio; dieses bleibt für
              // Mehrstudio-Mitarbeitende die Quelle der angezeigten Planung.
              loadedFromStudioId: existing.loadedFromStudioId,
            })
          })
        })
        this.allEmployees = Array.from(allEmpsMap.values())

        const shiftSourceStudioIds = {}
        let mergedShifts = []
        results.forEach((res) => {
          res.shifts.forEach((shift) => {
            const shiftId = String(shift?._id || "")
            if (shiftId) shiftSourceStudioIds[shiftId] = String(res.studioId)
          })
          mergedShifts = mergedShifts.concat(res.shifts)
        })
        this.shiftSourceStudioIds = shiftSourceStudioIds
        this.assignedPlanData = mergedShifts.filter((p) => p.assignedEmployee)
        this.unassignedShifts = mergedShifts.filter((p) => !p.assignedEmployee)

        let mergedExtShifts = []
        results.forEach((res) => {
          mergedExtShifts = mergedExtShifts.concat(res.externalShifts)
        })
        this.externalShifts = mergedExtShifts

        const mergedPreviousContext = {
          shifts: [],
          streakMap: {},
          weekendStats: {},
        }
        results.forEach((res) => {
          if (res.prevContext) {
            if (Array.isArray(res.prevContext.shifts)) {
              mergedPreviousContext.shifts.push(...res.prevContext.shifts)
            }
            if (res.prevContext.streakMap) {
              Object.entries(res.prevContext.streakMap).forEach(([employeeId, streak]) => {
                mergedPreviousContext.streakMap[employeeId] = Math.max(Number(mergedPreviousContext.streakMap[employeeId]) || 0, Number(streak) || 0)
              })
            }
            if (res.prevContext.weekendStats) {
              Object.entries(res.prevContext.weekendStats).forEach(([employeeId, stats]) => {
                const current = mergedPreviousContext.weekendStats[employeeId] || { count: 0, weeks: [] }
                const weeks = [...new Set([...(current.weeks || []), ...((stats && stats.weeks) || [])])]
                mergedPreviousContext.weekendStats[employeeId] = { count: weeks.length, weeks }
              })
            }
          }
        })
        this.previousContext = mergedPreviousContext

        this.prepareEmployees()
        // Die ergänzende Stundenberechnung läuft bewusst erst nach dem Plan.
        // Ihr Ausfall oder eine langsame Antwort hält Schichten und Mitarbeiter
        // dadurch nicht mehr im Ladezustand fest.
        void this.loadWorkHoursSummaries(studiosToFetch, requestId)
      } catch (err) {
        if (requestId !== this.refreshRequestId) return
        this.workHoursSummaryLoading = false
        console.error("Fehler beim Laden der Schichtdaten", err)
        this.$toast.error("Konnte Schichtplan nicht laden.")
      } finally {
        if (requestId === this.refreshRequestId) this.loading = false
      }
    },
    async loadWorkHoursSummaries(studiosToFetch, requestId) {
      try {
        const response = await this.withTimeout(
          api.shiftManagement.fetchWorkHoursSummaries(studiosToFetch, this.startDate, this.endDate),
          this.workHoursSummaryTimeoutMs,
          "Die Sollstunden-Berechnung hat zu lange gedauert.",
        )
        if (requestId !== this.refreshRequestId) return
        if (!response || !response.summaries || typeof response.summaries !== "object") {
          throw new Error("Zentrale Stundenübersicht fehlt.")
        }
        this.workHoursSummaries = response.summaries
        this.workHoursSummaryWarning = ""
      } catch (error) {
        if (requestId !== this.refreshRequestId) return
        console.warn("Zentrale Stundenübersicht konnte nicht geladen werden:", error)
        this.workHoursSummaries = {}
        this.workHoursSummaryWarning =
          "Der Einsatzplan ist vollständig geladen. Soll- und Planstunden konnten nicht zuverlässig berechnet werden; der Assistent bleibt gesperrt."
      } finally {
        if (requestId !== this.refreshRequestId) return
        this.workHoursSummaryLoading = false
        this.prepareEmployees()
      }
    },
    async retryWorkHoursSummaries() {
      if (this.workHoursSummaryLoading) return
      this.workHoursSummaryLoading = true
      this.workHoursSummaryWarning = ""
      await this.loadWorkHoursSummaries([this.studioId, ...this.activeAdditionalStudioIds], this.refreshRequestId)
      if (this.canUseAssistant) this.$toast.success("Sollstunden wurden neu berechnet.")
    },
    async executeDelete() {
      if (!this.canClearEntirePlan) {
        this.confirmDeleteDialog = false
        this.$toast.error("Plan leeren ist mit eingeblendeten Zusatzstudios gesperrt.")
        return
      }
      if (!this.assignedPlanData.length && !this.unassignedShifts.length) {
        this.confirmDeleteDialog = false
        return
      }
      this.deleting = true
      try {
        // Zweite Schutzschicht: Selbst bei einem verzögerten Refresh oder
        // direktem Methodenaufruf werden nur Schichten des Hauptstudios gelöscht.
        const allShifts = [...this.assignedPlanData, ...this.unassignedShifts].filter((shift) => {
          const shiftId = String(shift?._id || "")
          const sourceStudioId = String(this.shiftSourceStudioIds[shiftId] || shift?.studio?._id || shift?.studio || "")
          return sourceStudioId === String(this.studioId)
        })
        if (!allShifts.length) {
          this.confirmDeleteDialog = false
          return
        }
        const promises = allShifts.map((s) => api.shiftManagement.deleteShift(s._id || s))
        await Promise.all(promises)
        this.$toast.success("Plan erfolgreich geleert.")
        this.confirmDeleteDialog = false
        await this.refreshData()
      } catch (err) {
        console.error("Fehler beim Löschen:", err)
        this.$toast.error("Fehler beim Löschen des Plans.")
      } finally {
        this.deleting = false
      }
    },
    async executeDeleteUnassigned() {
      if (!this.unassignedShifts.length) {
        this.confirmDeleteUnassignedDialog = false
        return
      }
      this.deletingUnassigned = true
      try {
        await api.shiftManagement.deleteUnassignedShifts(this.studioId, this.startDate, this.endDate)
        this.$toast.success("Alle offenen Schichten erfolgreich gelöscht.")
        this.confirmDeleteUnassignedDialog = false
        await this.refreshData()
      } catch (err) {
        console.error("Fehler beim Löschen der offenen Schichten:", err)
        this.$toast.error("Fehler beim Löschen.")
      } finally {
        this.deletingUnassigned = false
      }
    },
    readLocalPlanningSettings(settingsKey) {
      try {
        const localSettings = JSON.parse(localStorage.getItem(settingsKey)) || {}
        return {
          ...localSettings,
          // Legacy-LocalStorage ist nicht monatsbezogen und darf deshalb
          // kein Planungsprozent überschreiben.
          percentages: {},
        }
      } catch (error) {
        console.warn(`Ungültige lokale Planungseinstellungen (${settingsKey}) werden ignoriert:`, error)
        return {}
      }
    },
    prepareEmployees() {
      let assignedUserIds = [
        ...new Set(this.assignedPlanData.map((plan) => String(plan?.assignedEmployee?._id || plan?.assignedEmployee || "")).filter(Boolean)),
      ]

      const activeStudios = [this.studioId, ...this.activeAdditionalStudioIds]
      const sourceEmployees = this.allEmployees.filter((emp) => activeStudios.includes(String(emp.loadedFromStudioId)))

      sourceEmployees.forEach((e) => {
        const uId = String(e.user?._id || e.user || "")
        if (!uId) return
        const empStudioId = e.loadedFromStudioId || this.studioId
        const backendSettings = this.getEffectiveMonthSettings(e, empStudioId)
        const settingsKey = `planningSettings_${e._id}_${empStudioId}`
        const localSettings = this.readLocalPlanningSettings(settingsKey)
        const savedSettings = Object.keys(backendSettings).length > 0 ? backendSettings : localSettings

        if ((savedSettings.isReadyForPlanning || this.manuallyAddedEmployeeIds.includes(uId)) && !assignedUserIds.includes(uId)) {
          assignedUserIds.push(uId)
        }
      })

      // Generiere die Liste der eingeblendeten Mitarbeiter
      let missingSummaryCount = 0
      this.assignedEmployees = assignedUserIds
        .map((userId) => {
          const employeeData = sourceEmployees.find((e) => String(e.user?._id || e.user || "") === userId)
          if (!employeeData) return null

          const empStudioId = employeeData.loadedFromStudioId || this.studioId
          const backendSettings = this.getEffectiveMonthSettings(employeeData, empStudioId)
          const settingsKey = `planningSettings_${employeeData._id}_${empStudioId}`
          const localSettings = this.readLocalPlanningSettings(settingsKey)
          const savedSettings = Object.keys(backendSettings).length > 0 ? backendSettings : localSettings
          const planningSettings = {
            percentages: savedSettings.percentages || {},
            recurringAbsence: savedSettings.recurringAbsence || [],
            recurringExternalStudio: savedSettings.recurringExternalStudio || [],
            isReadyForPlanning: savedSettings.isReadyForPlanning || false,
            customSollAdjustment: savedSettings.customSollAdjustment || 0,
            longCommute: savedSettings.longCommute === true,
            groupShifts: savedSettings.groupShifts === true,
            fixedShifts: savedSettings.fixedShifts || {},
            preferredPartners: savedSettings.preferredPartners || [],
            offsetOvertime: savedSettings.offsetOvertime === true,
            isApprentice: typeof savedSettings.isApprentice === "boolean" ? savedSettings.isApprentice : employeeData.isApprentice,
            schoolDays: Array.isArray(savedSettings.schoolDays) ? savedSettings.schoolDays : employeeData.schoolDays || [],
          }

          // Filter Ferien und Freiwünsche/Schichtwünsche für diesen Monat
          const rawVacations = (employeeData.holidayEntries || []).filter((e) => {
            if (e.status && e.status !== "accepted") return false
            const dates = e.dates
            if (!dates || !dates.length) return false
            const start = this.$moment(dates[0].date)
            const end = this.$moment(dates[dates.length - 1].date)
            return start.isSameOrBefore(this.endDate) && end.isSameOrAfter(this.startDate)
          })

          const vacations = rawVacations.map((h) => ({
            ...h,
            formattedRange:
              h.dates && h.dates.length
                ? `${this.$moment(h.dates[0].date).format("DD.MM")} - ${this.$moment(h.dates[h.dates.length - 1].date).format("DD.MM")}`
                : "",
          }))

          const freeWishes = (employeeData.freeWishEntries || []).filter((e) => {
            if (e.status && e.status !== "accepted") return false
            const date = this.$moment(e.day)
            return date.isBetween(this.startDate, this.endDate, null, "[]")
          })

          const shiftWishes = (employeeData.shiftWishEntries || []).filter((e) => {
            if (e.status && e.status !== "accepted") return false
            const date = this.$moment(e.day)
            return date.isBetween(this.startDate, this.endDate, null, "[]")
          })

          const centralSummary = this.workHoursSummaries[String(userId)]
          const hasCentralSummary = this.isValidWorkHoursSummary(centralSummary)
          if (!hasCentralSummary) missingSummaryCount += 1
          const fallbackSoll = Number(employeeData.sollStunden)
          const fallbackPlanned = Number(employeeData.plannedHours)
          return {
            ...employeeData,
            employeeWebsiteId: employeeData._id,
            _id: userId,
            isApprentice: planningSettings.isApprentice,
            schoolDays: planningSettings.schoolDays,
            vacations,
            freeWishes,
            shiftWishes,
            sollStunden: hasCentralSummary ? Number(centralSummary.total.netTargetHours) || 0 : Number.isFinite(fallbackSoll) ? fallbackSoll : 0,
            plannedHours: hasCentralSummary ? Number(centralSummary.total.plannedHours) || 0 : Number.isFinite(fallbackPlanned) ? fallbackPlanned : 0,
            hoursSummary: hasCentralSummary ? centralSummary : employeeData.hoursSummary || null,
            workHoursSummaryUnavailable: !hasCentralSummary,
            planningSettings,
          }
        })
        .filter((e) => e !== null)

      if (missingSummaryCount > 0 && !this.workHoursSummaryLoading && !this.workHoursSummaryWarning) {
        this.workHoursSummaryWarning =
          `${missingSummaryCount} Stundenübersicht${missingSummaryCount === 1 ? "" : "en"} fehlt vorübergehend. ` +
          "Die Schichten bleiben vollständig sichtbar; der Assistent bleibt bis zur erfolgreichen Berechnung gesperrt."
      }

      // NEU: Generiere die Liste der "verfügbaren" Mitarbeiter (die noch nicht angezeigt werden)
      this.availableEmployeesToAdd = sourceEmployees
        .filter((e) => !assignedUserIds.includes(String(e.user._id || e.user)))
        .map((e) => ({
          _id: String(e.user._id || e.user),
          fullName: e.fullName,
          picture: e.picture,
        }))
        .sort((a, b) => a.fullName.localeCompare(b.fullName))
    },
    onAddEmployee(userId) {
      if (!this.manuallyAddedEmployeeIds.includes(userId)) {
        this.manuallyAddedEmployeeIds.push(userId)
        this.prepareEmployees() // Roster mit dem zusätzlichen Mitarbeiter neu aufbereiten
      }
    },
    getEffectiveMonthSettings(employee, studioId) {
      const studioSettings = (employee.shiftPlanningSettings && employee.shiftPlanningSettings[studioId]) || {}
      if (Object.keys(studioSettings).length === 0) return {}
      const { monthlyOverrides, ...baseSettings } = studioSettings
      const monthlySettings = (monthlyOverrides && monthlyOverrides[this.monthId]) || {}
      return {
        ...baseSettings,
        ...monthlySettings,
        percentages: {
          ...(monthlySettings.percentages || {}),
        },
        fixedShifts: {
          ...(baseSettings.fixedShifts || {}),
          ...(monthlySettings.fixedShifts || {}),
        },
      }
    },
    async onMonthlySettingsSaved(payload) {
      this.employeeSettingsDialog = false
      this.settingsSavedEmployeeName = payload.employeeName || "den Mitarbeiter"
      if (payload.userId && payload.summary) this.$set(this.workHoursSummaries, payload.userId, payload.summary)

      try {
        await this.refreshData()
      } finally {
        this.settingsSavedDialog = true
      }
    },
    openAssistantAfterSettings() {
      this.settingsSavedDialog = false
      if (!this.canUseAssistant) {
        this.$toast.info("Die Sollstunden werden noch geprüft. Öffne den Assistenten danach erneut.")
        return
      }
      this.editMode = true
    },
  },
}
</script>

<style scoped>
.md-root {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  background: #1a1a1a;
}
.md-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 20px;
  background: rgba(0, 0, 0, 0.4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.md-back-btn {
  background: rgba(255, 255, 255, 0.06) !important;
}
.md-action-btn {
  background: rgba(255, 255, 255, 0.06) !important;
  color: white !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
}
.md-publish-btn {
  background: #69f0ae !important;
  color: #10241a !important;
}
.md-refresh-btn {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
}
.md-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.md-summary-warning {
  flex: 0 0 auto;
  border-radius: 0 !important;
  font-size: 0.78rem;
}
.md-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* LIGHT MODE STYLES OVERRIDES */
.md-root.light-mode {
  background: #f5f5f7;
}
.md-root.light-mode .md-header {
  background: rgba(255, 255, 255, 0.8) !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.md-root.light-mode .md-header .white--text {
  color: #1d1d1f !important;
}
.md-root.light-mode .md-header .text-caption {
  color: rgba(0, 0, 0, 0.45) !important;
}
.md-root.light-mode .md-back-btn {
  background: rgba(0, 0, 0, 0.04) !important;
  color: #1d1d1f !important;
}
.md-root.light-mode .md-action-btn {
  background: rgba(0, 0, 0, 0.04) !important;
  color: #1d1d1f !important;
}
.md-root.light-mode .md-publish-btn {
  background: #69f0ae !important;
  color: #10241a !important;
}
.md-root.light-mode .md-action-btn:hover {
  background: rgba(0, 0, 0, 0.08) !important;
}
.md-root.light-mode .md-loading p {
  color: rgba(0, 0, 0, 0.6) !important;
}
</style>
